import Vue from 'vue'
import configs from '@/config'

let resource = null

const actions = {
  getLeaderboardSlideSnapshot: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/leaderboards/slide/snapshot`,
  },
  getLeaderboardSlideTopN: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/leaderboards/slide/topn`,
  },
  getLeaderboardSlideAround: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/leaderboards/slide/around`,
  },
  getLeaderboardSelfPacedTopN: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/self-paced/leaderboard/slide/topn`,
  },
  getLeaderboardSelfPacedAround: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/self-paced/leaderboard/slide/around`,
  },
  getScore: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/scores`,
  },
  getStats: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/stats`,
  },
  getSelfPacedStats: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/self-paced/subject/slide/stats`,
  },
  getLeaderboardSlideSnapshotFromScoreboard: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/leaderboard/slide/snapshot`,
  },
  getLeaderboardSlideTopNFromScoreboard: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/leaderboard/slide/topn`,
  },
  getLeaderboardSlideAroundFromScoreboard: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/leaderboard/slide/around`,
  },
  getScoreFromScoreboard: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/subject/slide/score`,
  },
  getStatsFromScoreboard: {
    method: 'GET',
    url: `${configs.kvSyncApiUrl}/api/aha-sync/answers/subject/slide/stats`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

const getAggregation = (subject, teamScoringRule) => {
  if (subject === 'team') {
    if (teamScoringRule === 'average') {
      return 'average_score'
    }
    if (teamScoringRule === 'fastest') {
      return 'first_score'
    }
  }
  return 'total_score'
}

const slideWindowParams = ({ leaderboardSlideId, attemptPolicy }) => ({
  ...(leaderboardSlideId ? { leaderboardSlideId } : {}),
  ...(attemptPolicy ? { attemptPolicy } : {}),
})

const SCORING_BY_AGGREGATION = { total_score: 'total', average_score: 'average', first_score: 'fastest' }

const toScoreboardQuery = ({
  aggregation, slideIds, leaderboardSlideId, ...query
}) => ({
  ...query,
  ...(aggregation ? { scoring: SCORING_BY_AGGREGATION[aggregation] } : {}),
  ...(leaderboardSlideId ? { atSlideId: leaderboardSlideId } : { sumSlideIds: JSON.stringify(slideIds) }),
})

const toSlideIdsQuery = (query) => ({ ...query, slideIds: JSON.stringify(query.slideIds) })

const toLeaderboardBody = (res, subject) => {
  const items = res?.items || []
  if (subject === 'team') {
    return {
      teams: items.map((item) => ({
        id: item.id,
        name: item.name,
        totalPoint: item.score,
        index: item.rank,
        members: item.members || [],
      })),
    }
  }
  return {
    audiences: items.map((item) => ({
      audience: item.id,
      audienceName: item.name,
      audienceEmoji: item.emoji,
      teamId: item.teamId,
      totalPoint: item.score,
      index: item.rank,
    })),
  }
}

const getLeaderboardSlideSnapshot = async ({
  key,
  t,
  subject,
  leaderboardFromScoreboard,
}) => {
  const res = await (leaderboardFromScoreboard ? resource.getLeaderboardSlideSnapshotFromScoreboard({ key, t }) : resource.getLeaderboardSlideSnapshot({ key, t }))
  return toLeaderboardBody(res.body, subject)
}

// Self-paced: the live top-N DB read (not a snapshot); merged with the around-window in the store.
const getLeaderboardSlideTopN = async ({
  presentationId,
  presentationVersion,
  subject,
  teamScoringRule,
  slideIds,
  n,
  leaderboardSlideId,
  attemptPolicy,
  leaderboardFromScoreboard,
}) => {
  const query = {
    presentationId,
    presentationVersion,
    subject,
    aggregation: getAggregation(subject, teamScoringRule),
    slideIds,
    n,
    ...slideWindowParams({ leaderboardSlideId, attemptPolicy }),
  }
  if (!leaderboardFromScoreboard) {
    return toLeaderboardBody((await resource.getLeaderboardSlideTopN(toSlideIdsQuery(query))).body, subject)
  }
  const sent = toScoreboardQuery(query)
  const res = await (attemptPolicy ? resource.getLeaderboardSelfPacedTopN(sent) : resource.getLeaderboardSlideTopNFromScoreboard(sent))
  return toLeaderboardBody(res.body, subject)
}

// The audience's own rank-neighbourhood (k rows centred on subjectId), so their row survives the topn cap.
const getLeaderboardSlideAround = async ({
  presentationId,
  presentationVersion,
  subject,
  teamScoringRule,
  slideIds,
  subjectId,
  k,
  leaderboardSlideId,
  attemptPolicy,
  leaderboardFromScoreboard,
}) => {
  const query = {
    presentationId,
    presentationVersion,
    subject,
    aggregation: getAggregation(subject, teamScoringRule),
    slideIds,
    subjectId,
    k,
    ...slideWindowParams({ leaderboardSlideId, attemptPolicy }),
  }
  if (!leaderboardFromScoreboard) {
    return toLeaderboardBody((await resource.getLeaderboardSlideAround(toSlideIdsQuery(query))).body, subject)
  }
  const sent = toScoreboardQuery(query)
  const res = await (attemptPolicy ? resource.getLeaderboardSelfPacedAround(sent) : resource.getLeaderboardSlideAroundFromScoreboard(sent))
  return toLeaderboardBody(res.body, subject)
}

const getScore = async ({
  presentationId,
  presentationVersion,
  subject,
  subjectId,
  slideIds,
  aggregation,
  teamScoringRule,
  statsFromScoreboard,
}) => {
  const query = {
    presentationId,
    presentationVersion,
    subject,
    subjectId,
    slideIds,
    aggregation: aggregation || getAggregation(subject, teamScoringRule),
  }
  if (!statsFromScoreboard) {
    return (await resource.getScore(toSlideIdsQuery(query))).body
  }
  return (await resource.getScoreFromScoreboard(toScoreboardQuery(query))).body
}

const getStats = async ({
  presentationId,
  presentationVersion,
  subject,
  subjectId,
  slideIds,
  leaderboardSlideId,
  attemptPolicy,
  selfPacedAttemptId,
  statsFromScoreboard,
}) => {
  const query = {
    presentationId,
    presentationVersion,
    subject,
    subjectId,
    slideIds,
    ...slideWindowParams({ leaderboardSlideId, attemptPolicy }),
    ...(attemptPolicy && selfPacedAttemptId ? { selfPacedAttemptId } : {}),
  }
  if (!statsFromScoreboard) {
    return (await resource.getStats(toSlideIdsQuery(query))).body
  }
  const sent = toScoreboardQuery(query)
  return (await (attemptPolicy ? resource.getSelfPacedStats(sent) : resource.getStatsFromScoreboard(sent))).body
}

export default {
  initResource,
  getLeaderboardSlideSnapshot,
  getLeaderboardSlideTopN,
  getLeaderboardSlideAround,
  getScore,
  getStats,
}
